import {
  IBidRequest,
  IBidResponse,
  IBidsBackHandlerResponse,
  IPrebid,
  IPrebidAdUnit
} from '../../../typings/IPrebid'
import {
  BID_REQUEST_WAIT,
  CPM_TOFIXED_LIMIT,
  DEFAULT_MAX_DURATION
} from '../../constants/video'
import { Appnexus, IndexExchangeVideo } from '../../enums/VideoBidder'
import { API, DeliveryMethod, Linearity, Protocol } from '../../enums/OpenRTB'
import addVideoCacheKeys from '../../helpers/cache'
import VideoBidRequest, { getVideoFloor } from './VideoBidRequest'
import { VideoBidder } from '../../enums/Bidder'
import { Device } from '../../helpers/index'
import { IVideoAdTagOptions } from '../../../typings/IVideoOptions'

interface INamespacedBids {
  [key: string]: {
    bids: IBidResponse[]
  }
}

export function getAdjustedCPM(cpm: number): string {
  let adjustedCPM

  /* eslint-disable no-magic-numbers */
  if (cpm <= 20) {
    // 0.01 - 20.00 at 0.01
    adjustedCPM = (Math.floor(cpm * 100) / 100).toFixed(2)
  } else if (cpm > 20 && cpm <= 40) {
    // 20 - 40 at 0.05
    adjustedCPM = (Math.floor(cpm * 20) / 20).toFixed(2)
  } else if (cpm > 40 && cpm <= 200) {
    // 40 - 200 at 0.25
    adjustedCPM = (Math.floor(cpm * 4) / 4).toFixed(2)
  } else if (cpm > 200.0) {
    // Cap at 200
    adjustedCPM = (200).toFixed(2)
  }
  /* eslint-enable no-magic-numbers */

  return adjustedCPM
}

export default class VideoPrebidManager {
  public readonly options: IVideoAdTagOptions
  public readonly overrides: any
  public readonly prebid: IPrebid

  constructor(
    options: IVideoAdTagOptions,
    private readonly webModel: IWebViewModel,
    prebid = window.pbjs,
    overrides: any = {}
  ) {
    this.options = options
    this.overrides = overrides
    this.prebid = prebid

    //const config = this.options.config
    const tBids = this.options.testBids
    const tBidKeys = Object.keys(tBids).filter((bidder) => tBids[bidder])

    const videoDebuggingEnabled = !!(options.debug || tBidKeys.length)
    if (videoDebuggingEnabled) {
      prebid.setConfig({
        debugging: {
          bidders: tBidKeys,
          bids: tBidKeys.map((bidder) => ({
            bidder,
            cpm: 20
          })),
          enabled: videoDebuggingEnabled
        }
      })
    }
  }

  /**
   * GAM requires bids to be properly named/formatted to include the
   * the CPM and VideoCacheKey with a key including the bidderCode. The method
   * formats manual bids or queues bid requests in Prebid, caches the
   * VideoCacheKey, and returns the formatted DFP custom params.
   *
   * Example output:
   * {
   *   hb_vpb_indexExchange: 5.00,
   *   hb_uuid_indexExchange: abcd12345efgh6789
   *   hb_vpb_rubicon: 10.00,
   *   hb_uuid_rubicon: 9876hgfe54321dcba
   * }
   */
  public async getFormattedBidsFromPrebidAuction(): Promise<{}> {
    return new Promise((resolve, reject) => {
      this.prebid.bidderSettings = {
        ...this.prebid.bidderSettings,
        ...this.bidderSettings
      }

      this.prebid.que.push(() => {
        this.prebid.requestBids({
          adUnits: [this.adUnits],
          auctionId: 'video',
          bidsBackHandler: (namespacedBids: IBidsBackHandlerResponse = {}) => {
            const floor = getVideoFloor(Device.isMobile)
            const validBids: IBidsBackHandlerResponse = {}

            // Filter out invalids bids for each adunit (namespace)
            Object.keys(namespacedBids).forEach((key) => {
              validBids[key] = {
                bids: namespacedBids[key].bids.filter(
                  (bid) => bid.cpm >= floor && !!(bid.vastUrl || bid.vastXml)
                )
              }
            })

            const bids = Object.keys(validBids)
              .reduce((acc, key) => [...acc, ...validBids[key].bids], [])
              .map((bid: IBidResponse) => bid.cpm)
            const maxBid = Math.max(0, ...bids)

            if (maxBid === 0) {
              return this.webModel.google
                ? resolve({ hb_bid: maxBid })
                : resolve({ error: 'No bids returned', maxBid })
            }

            this.cacheAndFormatBidsAsDFPCustomParams(validBids, maxBid).then(
              resolve,
              reject
            )
          },
          timeout: BID_REQUEST_WAIT
        })
      })
    })
  }

  get adUnits(): IPrebidAdUnit {
    const playerSize: AdSize = [
      this.options.contentWidth,
      this.options.contentHeight
    ]
    return {
      bids: this.bidRequests,
      code: this.options.adunitId,
      mediaTypes: {
        video: {
          api: [API.VPAID_1_0, API.VPAID_2_0],
          context: 'instream',
          delivery: [DeliveryMethod.progressive],
          linearity: Linearity.linear,
          maxduration: DEFAULT_MAX_DURATION,
          mimes: this.options.mimes,
          playerSize,
          protocols: [
            Protocol.VAST_2_0,
            Protocol.VAST_3_0,
            Protocol.VAST_2_0_wrapper,
            Protocol.VAST_3_0_wrapper,
            Protocol.VAST_4_0,
            Protocol.VAST_4_0_wrapper
          ],
          sizes: [playerSize]
        }
      }
    }
  }

  get bidderSettings(): object {
    return {
      [IndexExchangeVideo.id]: {
        adserverTargeting: [
          {
            key: 'hb_vpmp_ixVideo',
            val(bidResponse) {
              return bidResponse.hb_vpmp_ixVideo
            }
          }
        ]
      }
    }
  }

  get bidRequests(): IBidRequest[] {
    //const settings = this.options.config.site
    const bidRequest = new VideoBidRequest(
      this.options,
      this.webModel,
      this.overrides
    )
    const req: IBidRequest[] = []

    // Note: These are in the order we want them to be requested. Do not reorder unless you know what's up.
    if (this.webModel.index) {
      req.push(bidRequest.indexExchange)
    }

    if (this.webModel.rubicon) {
      req.push(bidRequest.rubicon)
    }

    if (this.webModel.appnexus) {
      req.push(bidRequest.appnexus)
    }

    if (this.webModel.oneVideo) {
      req.push(bidRequest.oneVideo)
    }

    if (this.webModel.brealtime) {
      // NOTE: Disabling EMX video bidding until they fix their outstream/instream bug
      // req.push(bidRequest.emxDigital)
    }

    if (this.webModel.openx) {
      req.push(bidRequest.openx)
    }

    if (this.webModel.pubmatic) {
      req.push(bidRequest.pubmatic)
    }

    if (this.webModel.pulsepoint) {
      req.push(bidRequest.pulsepoint)
    }

    return req
  }

  public async cacheAndFormatBidsAsDFPCustomParams(
    namespacedBids?: IBidsBackHandlerResponse,
    maxBid?: number
  ): Promise<{}> {
    const adunitId = this.options.adunitId

    if (
      namespacedBids &&
      namespacedBids[adunitId] &&
      namespacedBids[adunitId].bids
    ) {
      const { bids } = namespacedBids[adunitId]
      const validBids = bids.filter(
        (bid) => bid.cpm > 0 && !!(bid.vastUrl || bid.vastXml)
      )
      const cachedBids = await addVideoCacheKeys(validBids)
      const substrLength = 20

      return cachedBids.reduce(
        (acc, bid) => {
          if (bid.bidderCode === VideoBidder.appnexus) {
            bid.bidderCode = (Appnexus.aliasId as unknown) as VideoBidder
          }

          // We are limiting the length of bidderCode so that we don't exceed
          // the length of limit of GAM key/value keys.
          let bidderCode = (bid.bidderCode as unknown) as string
          bidderCode = bidderCode.substring(0, substrLength)

          bid = this.cleanTargeting(bid)

          const adjustedCPM = getAdjustedCPM(bid.cpm)
          return {
            ...acc,
            ...bid.adserverTargeting,
            [`hb_vpb_${bidderCode}`]: adjustedCPM,
            [`hb_uuid_${bidderCode}`]: bid.videoCacheKey
          }
        },
        {
          hb_bid:
            typeof maxBid === 'number'
              ? maxBid.toFixed(CPM_TOFIXED_LIMIT)
              : 'no_bid'
        }
      )
    } else {
      return {
        hb_bid: 'no_bid'
      }
    }
  }

  /**
   * We need to remove the standard adserverTargeting parameters so we don't
   * accidentally trigger display ads' line-items in GAM.
   * @param bid IBidResponse
   */
  private cleanTargeting(bid: IBidResponse) {
    if (bid && bid.adserverTargeting) {
      delete bid.adserverTargeting.hb_adid
      delete bid.adserverTargeting.hb_bidder
      delete bid.adserverTargeting.hb_pb
      delete bid.adserverTargeting.hb_size
    }
    return bid
  }
}
